import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useData } from '../contexts/DataContext';
import { getTopProducts } from '../utils/dataProcessing';

const PlatformView = ({ userContext }) => {
  const {
    loading,
    error,
    platformStats,
    storeStats,
    processedData,
    platformMapping
  } = useData();

  const [selectedPlatform, setSelectedPlatform] = useState('');

  // Get available platforms
  const platforms = useMemo(() => {
    return Object.keys(platformStats).filter(p => p !== 'Unknown').sort();
  }, [platformStats]);

  // Set initial platform
  useEffect(() => {
    if (!selectedPlatform && platforms.length > 0) {
      if (userContext?.platform && platforms.includes(userContext.platform)) {
        setSelectedPlatform(userContext.platform);
      } else {
        setSelectedPlatform(platforms[0]);
      }
    }
  }, [platforms, userContext, selectedPlatform]);

  // Get current platform stats
  const currentPlatformStats = useMemo(() => {
    return platformStats[selectedPlatform] || {
      totalOrders: 0,
      totalQtyOrdered: 0,
      totalQtyPicked: 0,
      fulfillmentRate: 0,
      storeCount: 0,
      stores: [],
      products: {},
      dailyVolumes: {}
    };
  }, [platformStats, selectedPlatform]);

  // Get stores for current platform
  const storesForPlatform = useMemo(() => {
    return currentPlatformStats.stores
      .map(store => ({
        store,
        ...storeStats[store]
      }))
      .filter(store => store.storeName)
      .sort((a, b) => b.totalOrders - a.totalOrders);
  }, [currentPlatformStats, storeStats]);

  // Get top products
  const topProducts = useMemo(() => {
    return getTopProducts(currentPlatformStats.products, 5);
  }, [currentPlatformStats]);

  // Get daily volume data
  const dailyVolumeData = useMemo(() => {
    return Object.entries(currentPlatformStats.dailyVolumes)
      .map(([date, count]) => ({ date, orders: count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-30); // Last 30 days
  }, [currentPlatformStats]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">
          Error loading data: {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Platform Dashboard
          </Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Select Platform</InputLabel>
            <Select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              label="Select Platform"
            >
              {platforms.map(platform => (
                <MenuItem key={platform} value={platform}>{platform}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Summary Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Orders
              </Typography>
              <Typography variant="h4">
                {currentPlatformStats.totalOrders.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Fulfillment Rate
              </Typography>
              <Typography variant="h4">
                {(currentPlatformStats.fulfillmentRate * 100).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Stores
              </Typography>
              <Typography variant="h4">
                {currentPlatformStats.storeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Qty Ordered
              </Typography>
              <Typography variant="h4">
                {Math.round(currentPlatformStats.totalQtyOrdered).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Daily Order Volume Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Daily Order Volume
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyVolumeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="orders" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Products */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top Products
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Qty Ordered</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topProducts.map((product) => (
                    <TableRow key={product.sku}>
                      <TableCell>{product.sku}</TableCell>
                      <TableCell align="right">{Math.round(product.totalQtyOrdered)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Stores Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Stores Performance
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Store</TableCell>
                    <TableCell>Store Name</TableCell>
                    <TableCell align="right">Total Orders</TableCell>
                    <TableCell align="right">Qty Ordered</TableCell>
                    <TableCell align="right">Qty Picked</TableCell>
                    <TableCell align="right">Fulfillment Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storesForPlatform.map((store) => (
                    <TableRow key={store.store}>
                      <TableCell>{store.store}</TableCell>
                      <TableCell>{store.storeName}</TableCell>
                      <TableCell align="right">{store.totalOrders}</TableCell>
                      <TableCell align="right">{Math.round(store.totalQtyOrdered)}</TableCell>
                      <TableCell align="right">{Math.round(store.totalQtyPicked)}</TableCell>
                      <TableCell align="right">{(store.fulfillmentRate * 100).toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PlatformView;